import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import ProfileCompletionGate from "./ProfileCompletionGate";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  House, GraduationCap, Books, Users, ChartBar, ChatCircle, CreditCard, List, SignOut, UserCircle, Gear, CaretDown, Brain, EnvelopeSimple, Image as ImageIcon, Briefcase, VideoCamera, Certificate, FileText, CalendarCheck, ShieldCheck, UsersThree, Eye, Bell, UserPlus
} from "@phosphor-icons/react";

const SBL_LOGO = "/sbl-logo.jpeg";

const navItems = {
  super_admin: [
    { label: "Dashboard", icon: House, path: "/dashboard", perm: "view_dashboard" },
    { label: "Public-Site CMS", icon: ShieldCheck, path: "/admin", perm: "manage_site_cms" },
    { label: "Programmes", icon: GraduationCap, path: "/programmes", perm: "manage_programmes" },
    { label: "Courses", icon: Books, path: "/courses", perm: "manage_courses" },
    { label: "Progress", icon: ChartBar, path: "/progress", perm: "view_analytics" },
    { label: "Users", icon: Users, path: "/admin/users", perm: "manage_users" },
    { label: "Leads", icon: UserPlus, path: "/admin/leads", perm: "manage_leads" },
    { label: "Roles", icon: ShieldCheck, path: "/admin/roles", perm: "manage_roles" },
    { label: "Analytics", icon: ChartBar, path: "/admin/analytics", perm: "view_analytics" },
    { label: "Payments", icon: CreditCard, path: "/payments", perm: "manage_payments" },
    { label: "Messages", icon: EnvelopeSimple, path: "/messages", perm: "manage_messages" },
    { label: "Live Meeting", icon: VideoCamera, path: "/meetings", perm: "manage_meetings" },
    { label: "Certificates", icon: Certificate, path: "/certificates", perm: "manage_certificates" },
    { label: "Transcripts", icon: FileText, path: "/transcripts", perm: "manage_transcripts" },
    { label: "Attendance", icon: CalendarCheck, path: "/attendance", perm: "manage_attendance" },
    { label: "AI Tools", icon: Brain, path: "/ai-tools", perm: "manage_ai_tools" },
    { label: "Careers", icon: Briefcase, path: "/careers", perm: "manage_careers" },
    { label: "Forum", icon: ChatCircle, path: "/forum", perm: "manage_forum" },
    { label: "Community", icon: UsersThree, path: "/community", perm: "manage_community" },
    { label: "Moderation", icon: ShieldCheck, path: "/admin/moderation", perm: "manage_moderation" },
    { label: "POD", icon: UsersThree, path: "/pod/webinars", perm: "access_pod" },
    { label: "Designers", icon: FileText, path: "/admin/designers/transcript", perm: "manage_designers" },
    { label: "Letter Templates", icon: FileText, path: "/admin/admission-templates", perm: "manage_admission_templates" },
    { label: "Admission Letters", icon: FileText, path: "/admin/admission-letters", perm: "manage_admission_templates" },
    { label: "Feedback", icon: ChatCircle, path: "/admin/feedback", perm: "view_analytics" },
    { label: "Storage", icon: FileText, path: "/admin/storage", perm: "manage_site_cms" },
  ],
  admin: [
    { label: "Dashboard", icon: House, path: "/dashboard", perm: "view_dashboard" },
    { label: "Public-Site CMS", icon: ShieldCheck, path: "/admin", perm: "manage_site_cms" },
    { label: "Programmes", icon: GraduationCap, path: "/programmes", perm: "manage_programmes" },
    { label: "Courses", icon: Books, path: "/courses", perm: "manage_courses" },
    { label: "Users", icon: Users, path: "/admin/users", perm: "manage_users" },
    { label: "Leads", icon: UserPlus, path: "/admin/leads", perm: "manage_leads" },
    { label: "Analytics", icon: ChartBar, path: "/admin/analytics", perm: "view_analytics" },
    { label: "Payments", icon: CreditCard, path: "/payments", perm: "manage_payments" },
    { label: "Messages", icon: EnvelopeSimple, path: "/messages", perm: "manage_messages" },
    { label: "Live Meeting", icon: VideoCamera, path: "/meetings", perm: "manage_meetings" },
    { label: "Certificates", icon: Certificate, path: "/certificates", perm: "manage_certificates" },
    { label: "Transcripts", icon: FileText, path: "/transcripts", perm: "manage_transcripts" },
    { label: "Attendance", icon: CalendarCheck, path: "/attendance", perm: "manage_attendance" },
    { label: "Forum", icon: ChatCircle, path: "/forum", perm: "manage_forum" },
    { label: "POD", icon: UsersThree, path: "/pod/webinars", perm: "access_pod" },
    { label: "Designers", icon: FileText, path: "/admin/designers/transcript", perm: "manage_designers" },
    { label: "Letter Templates", icon: FileText, path: "/admin/admission-templates", perm: "manage_admission_templates" },
    { label: "Admission Letters", icon: FileText, path: "/admin/admission-letters", perm: "manage_admission_templates" },
  ],
  instructor: [
    { label: "Dashboard", icon: House, path: "/dashboard", perm: "view_dashboard" },
    { label: "My Courses", icon: Books, path: "/courses", perm: "manage_courses" },
    { label: "Messages", icon: EnvelopeSimple, path: "/messages", perm: "manage_messages" },
    { label: "Live Meeting", icon: VideoCamera, path: "/meetings", perm: "manage_meetings" },
    { label: "Certificates", icon: Certificate, path: "/certificates", perm: "manage_certificates" },
    { label: "Transcripts", icon: FileText, path: "/transcripts", perm: "manage_transcripts" },
    { label: "Attendance", icon: CalendarCheck, path: "/attendance", perm: "manage_attendance" },
    { label: "Forum", icon: ChatCircle, path: "/forum", perm: "manage_forum" },
    { label: "AI Tools", icon: Brain, path: "/ai-tools", perm: "manage_ai_tools" },
  ],
  student: [
    { label: "Dashboard", icon: House, path: "/dashboard", perm: null },
    { label: "My Programmes", icon: GraduationCap, path: "/my-programmes", perm: null },
    { label: "My Learning", icon: Books, path: "/courses", perm: null },
    { label: "Progress", icon: ChartBar, path: "/progress", perm: null },
    { label: "Messages", icon: EnvelopeSimple, path: "/messages", perm: null },
    { label: "Live Meeting", icon: VideoCamera, path: "/meetings", perm: null },
    { label: "Certificates", icon: Certificate, path: "/certificates", perm: null },
    { label: "Transcripts", icon: FileText, path: "/transcripts", perm: null },
    { label: "Attendance", icon: CalendarCheck, path: "/attendance", perm: null },
    { label: "Forum", icon: ChatCircle, path: "/forum", perm: null },
    { label: "Community", icon: UsersThree, path: "/community", perm: null },
    { label: "Payments", icon: CreditCard, path: "/payments", perm: null },
  ],
  finance: [
    { label: "Dashboard", icon: House, path: "/dashboard", perm: "view_dashboard" },
    { label: "Payments", icon: CreditCard, path: "/payments", perm: "manage_payments" },
  ],
};

function SidebarContent({ items, location, onClose }) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-6 pb-4">
        <Link to="/" onClick={onClose} className="flex items-center gap-2">
          <img src={SBL_LOGO} alt="SBL" className="w-8 h-8 object-contain rounded-sm" />
          <span className="text-base font-bold tracking-tight text-slate-950 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            School Builder Lab Africa
          </span>
        </Link>
      </div>
      <Separator />
      <nav className="flex-1 overflow-y-auto p-4 space-y-1" data-testid="sidebar-nav-scroll">
        {items.map((item) => {
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-blue-800 text-white"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
            >
              <Icon size={20} weight={isActive ? "fill" : "regular"} />
              {item.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [notifsOpen, setNotifsOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  // Section 9 — permission-based sidebar gating. super_admin gets ["*"] from
  // the backend so the "*" wildcard short-circuits everything.
  const [permissions, setPermissions] = useState([]);

  const refreshCounts = () => {
    api.get("/messages/unread-count").then(r => setUnreadMsgs(r.data?.count || 0)).catch(() => {});
    api.get("/notifications/unread-count").then(r => setUnreadNotifs(r.data?.count || 0)).catch(() => {});
  };

  useEffect(() => {
    refreshCounts();
    const interval = setInterval(refreshCounts, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Pull effective permissions once user is loaded. super_admin / admin get
    // the wildcard "*" so they see everything; custom roles are gated.
    if (!user) { setPermissions([]); return; }
    api.get("/auth/me/permissions").then(r => setPermissions(r.data?.permissions || [])).catch(() => setPermissions([]));
  }, [user?.id, user?.role]);

  const openNotifs = async () => {
    setNotifsOpen(true);
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data || []);
      // Mark all as read
      await api.post("/notifications/read-all");
      setUnreadNotifs(0);
    } catch {}
  };

  const actualRole = user?.role || "student";
  const canPreview = ["super_admin", "admin", "instructor"].includes(actualRole);
  const role = previewMode && canPreview ? "student" : actualRole;
  const accessLevel = user?.access_level;
  const canAccessPod = user?.can_access_pod === true || user?.role === "super_admin" || user?.role === "pod_subscriber" || user?.pod_access === true;
  // Free / applied students must not see Messages, Live Meeting, Community, Forum.
  const isRestricted = accessLevel === "free_only" || accessLevel === "applied";
  let items = navItems[role] || navItems.student;
  // Section 9 — permission-based gating. Students aren't permission-gated
  // (their nav has perm:null), so this only filters staff items.
  const hasWildcard = permissions.includes("*");
  if (!hasWildcard && role !== "student") {
    items = items.filter(it => !it.perm || permissions.includes(it.perm));
  }
  // Section D6 + free-vs-paid disparity — restrict programme browsing and gated areas.
  if (isRestricted) {
    items = items.filter(it => !["/messages", "/meetings", "/community", "/forum"].includes(it.path));
  }
  // Free-only students (no programme enrolment at all) must not see the programme
  // catalogue browser either — they only get Free Courses access.
  if (accessLevel === "free_only") {
    items = items.filter(it => !["/programmes-catalogue", "/programmes"].includes(it.path));
  }
  // Hide POD entries from users without POD access (everyone except super_admin / pod_subscriber / granted users).
  if (!canAccessPod) {
    items = items.filter(it => !it.path.startsWith("/pod"));
  }

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-white overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:w-60 lg:w-64 flex-col border-r border-slate-200 bg-white">
        <SidebarContent items={items} location={location} onClose={() => {}} />
      </aside>

      {/* Main Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-14 border-b border-slate-200 bg-white/90 backdrop-blur-xl sticky top-0 z-50 flex items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="sm" data-testid="mobile-menu-btn">
                  <List size={20} />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64">
                <SidebarContent items={items} location={location} onClose={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-400 hidden sm:block">
              School Builder Lab Africa - Educational LMS
            </span>
          </div>

          <div className="flex items-center gap-2">
            {!isRestricted && (
            <Link to="/messages" className="relative p-2 text-slate-500 hover:text-slate-900 transition-colors" data-testid="messages-bell" title="Messages">
              <EnvelopeSimple size={20} />
              {unreadMsgs > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">{unreadMsgs > 9 ? "9+" : unreadMsgs}</span>
              )}
            </Link>
            )}
            <DropdownMenu open={notifsOpen} onOpenChange={(o) => (o ? openNotifs() : setNotifsOpen(false))}>
              <DropdownMenuTrigger asChild>
                <button className="relative p-2 text-slate-500 hover:text-slate-900 transition-colors" data-testid="notification-bell" title="Notifications">
                  <Bell size={20} />
                  {unreadNotifs > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">{unreadNotifs > 9 ? "9+" : unreadNotifs}</span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto rounded-none">
                <div className="px-3 py-2 border-b border-slate-200">
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Notifications</p>
                </div>
                {notifs.length === 0 ? (
                  <div className="px-3 py-8 text-center text-sm text-slate-400" data-testid="no-notifications">
                    No notifications yet
                  </div>
                ) : (
                  notifs.slice(0, 20).map((n, idx) => (
                    <Link
                      key={n.id}
                      to={n.source_url || "#"}
                      onClick={() => setNotifsOpen(false)}
                      className={`block px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 text-xs ${n.read ? "" : "bg-blue-50/40"}`}
                      data-testid={`notification-${idx}`}
                    >
                      <p className="font-medium text-slate-800 mb-0.5 flex items-center gap-1.5">
                        {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                        {n.type === "mention" ? "You were mentioned" : n.type}
                      </p>
                      <p className="text-slate-600 line-clamp-2">{n.text}</p>
                      <p className="text-[10px] text-slate-400 mt-1">
                        {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                      </p>
                    </Link>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
                <UserCircle size={22} className="text-slate-600" />
                <span className="hidden sm:inline text-sm font-medium text-slate-700">{user?.name}</span>
                <CaretDown size={14} className="text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
                <p className="text-xs text-blue-800 font-medium capitalize mt-0.5">{user?.role?.replace("_", " ")}</p>
              </div>
              <DropdownMenuSeparator />
              {canPreview && (
                <DropdownMenuItem onClick={() => setPreviewMode(!previewMode)} data-testid="preview-mode-btn">
                  <Eye size={16} className="mr-2" />
                  {previewMode ? "Exit Student View" : "Preview as Student"}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <SignOut size={16} className="mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-slate-50">
          {previewMode && (
            <div className="bg-amber-400 text-amber-900 px-4 py-2 flex items-center justify-between text-sm font-medium" data-testid="preview-mode-banner">
              <div className="flex items-center gap-2">
                <Eye size={16} weight="fill" />
                <span>Preview Mode — You are viewing as a student</span>
              </div>
              <button onClick={() => setPreviewMode(false)} className="text-xs underline font-semibold hover:text-amber-800">
                Exit Preview
              </button>
            </div>
          )}
          {children}
        </main>
      </div>
      <ProfileCompletionGate />
    </div>
  );
}
